import React, { useState, useEffect } from 'react';
import { FaTimes, FaMapMarkerAlt, FaCrosshairs } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { adminApi } from '../../services/api';

const ProjectModal = ({ isOpen, onClose, onSubmit, result, loading, clubs }) => {
    const [formData, setFormData] = useState({});
    const [geoLoading, setGeoLoading] = useState(false);
    const [geoError, setGeoError] = useState('');
    const [mapLink, setMapLink] = useState('');
    const [linkLoading, setLinkLoading] = useState(false);
    const [venueLoading, setVenueLoading] = useState(false);

    // Reverse geocode lat/lng → human-readable place name using OpenStreetMap
    const reverseGeocode = async (lat, lng) => {
        setVenueLoading(true);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
                { headers: { 'Accept-Language': 'en' } }
            );
            const data = await res.json();
            if (data && data.display_name) {
                // Use the short name if available, otherwise the full address
                const shortName = data.name || data.address?.amenity || data.address?.road || data.display_name.split(',')[0];
                const city = data.address?.city || data.address?.town || data.address?.village || '';
                const venueName = city ? `${shortName}, ${city}` : shortName;
                setFormData(prev => ({ ...prev, venue: venueName }));
                toast.success(`Venue auto-detected: ${venueName}`, { icon: '📍' });
            }
        } catch (err) {
            // Silently fail — user can still type venue manually
            console.warn('Reverse geocode failed:', err);
        } finally {
            setVenueLoading(false);
        }
    };

    const handleResolveLink = async () => {
        if (!mapLink) return;
        setLinkLoading(true);
        setGeoError('');
        const res = await adminApi.resolveMapLink(mapLink);
        setLinkLoading(false);
        if (res.success) {
            const lat = parseFloat(res.data.latitude);
            const lng = parseFloat(res.data.longitude);
            setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
            toast.success('Coordinates extracted successfully!');
            setMapLink('');
            reverseGeocode(lat, lng); // Auto-fill venue
        } else {
            setGeoError(res.message || 'Failed to extract coordinates from link');
        }
    };

    useEffect(() => {
        setFormData(result || {});
    }, [result, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const fetchGpsLocation = () => {
        if (!navigator.geolocation) {
            setGeoError("Geolocation is not supported by your browser");
            return;
        }
        
        setGeoLoading(true);
        setGeoError('');
        
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
                setGeoLoading(false);
                reverseGeocode(lat, lng); // Auto-fill venue
            },
            (error) => {
                setGeoError("Unable to retrieve your location");
                setGeoLoading(false);
            }
        );
    };

    const inputClasses = "w-full px-5 py-3.5 rounded-3xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900 placeholder:text-slate-400 placeholder:font-normal";
    const labelClasses = "block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1";

    return (
        <div className="fixed inset-0 z-[1000000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300" onClick={onClose}>
            <div className="bg-white rounded-3xl w-full max-w-lg overflow-y-auto max-h-[90vh] shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-slate-100 transform transition-all animate-premium" onClick={(e) => e.stopPropagation()}>
                <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-white sticky top-0 z-10">
                    <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                        Map Tactical Initiative
                    </h2>
                    <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all">
                        <FaTimes size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-6">
                    <div>
                        <label className={labelClasses}>Project Technical Name</label>
                        <input
                            type="text"
                            value={formData.name || ''}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            placeholder="Enter project name"
                            className={inputClasses}
                        />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className={labelClasses} style={{marginBottom: 0}}>Venue / Location Name</label>
                            {venueLoading && (
                                <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest flex items-center gap-1 animate-pulse">
                                    <span className="w-3 h-3 border border-indigo-500 border-t-transparent rounded-full animate-spin inline-block" />
                                    Detecting venue...
                                </span>
                            )}
                            {formData.venue && !venueLoading && (
                                <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest flex items-center gap-1">
                                    📍 Auto-detected
                                </span>
                            )}
                        </div>
                        <input
                            type="text"
                            value={formData.venue || ''}
                            onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                            placeholder="Auto-fills from GPS/Maps, or type manually"
                            className={`${inputClasses} ${venueLoading ? 'opacity-60 animate-pulse' : ''}`}
                            disabled={venueLoading}
                        />
                    </div>
                    
                    <div>
                        <label className={labelClasses}>Associated Unit</label>
                        <select
                            value={formData.club_id || ''}
                            onChange={(e) => setFormData({ ...formData, club_id: e.target.value })}
                            required
                            className={inputClasses}
                        >
                            <option value="">Select Target Club</option>
                            {clubs.map((club) => (
                                <option key={club.lion_ID} value={club.lion_ID}>{club.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="bg-slate-50/50 p-6 rounded-3xl border border-slate-100 space-y-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-2">
                            <span className="text-[10px] font-black tracking-[0.2em] text-slate-400 uppercase flex items-center gap-2">
                                <FaMapMarkerAlt className="text-indigo-500" /> Geographic Anchoring
                            </span>
                            <button
                                type="button"
                                onClick={fetchGpsLocation}
                                disabled={geoLoading || linkLoading}
                                className="text-[9px] font-bold uppercase tracking-widest bg-indigo-50 text-indigo-600 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1"
                            >
                                <FaCrosshairs /> {geoLoading ? 'Scanning...' : 'Fetch Device GPS'}
                            </button>
                        </div>
                        
                        {geoError && <div className="text-[10px] text-red-500 font-bold mb-2">{geoError}</div>}

                        <div className="flex gap-2">
                            <input 
                                type="url" 
                                value={mapLink} 
                                onChange={(e) => setMapLink(e.target.value)} 
                                placeholder="Paste a Google Maps URL... (e.g. maps.app.goo.gl/...)" 
                                className="flex-1 px-4 py-3 rounded-2xl border border-slate-100 bg-white text-xs font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500/20 outline-none placeholder:font-normal placeholder:text-slate-400"
                            />
                            <button
                                type="button"
                                onClick={handleResolveLink}
                                disabled={!mapLink || linkLoading || geoLoading}
                                className="px-5 py-3 bg-slate-900 text-white rounded-2xl text-[10px] uppercase tracking-widest font-black whitespace-nowrap disabled:opacity-50 hover:bg-black transition-colors"
                            >
                                {linkLoading ? 'Parsing...' : 'Extract'}
                            </button>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 pt-2">
                            <div>
                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest pl-1 mb-1 block">Latitude</label>
                                <input
                                    type="number"
                                    step="any"
                                    value={formData.latitude || ''}
                                    onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) })}
                                    className="w-full px-4 py-3 rounded-2xl border border-slate-100 bg-white text-xs font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                                    placeholder="40.7128"
                                />
                            </div>
                            <div>
                                <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest pl-1 mb-1 block">Longitude</label>
                                <input
                                    type="number"
                                    step="any"
                                    value={formData.longitude || ''}
                                    onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) })}
                                    className="w-full px-4 py-3 rounded-2xl border border-slate-100 bg-white text-xs font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                                    placeholder="-74.0060"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-4 pt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-4 text-slate-400 font-bold text-[10px] uppercase tracking-widest hover:text-slate-900 transition-colors"
                        >
                            Abort
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-[2] py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-slate-900/20 hover:bg-black hover:-translate-y-0.5 transition-all disabled:opacity-50"
                        >
                            {loading ? 'Processing...' : 'Commit Intelligence'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProjectModal;

